import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetContentCommentsQuery, useAddContentHistoryMutation, useAddContentHistoryTagsMutation, useUploadHistoryAttachmentMutation, useGetTagListWithTypeQuery, useDeleteContentHistoryMutation, UserRoleEnum } from '../../../generated/graphql';
import { useCurrentUser } from '../../../App.hooks';
import { Bug, HelpCircle, Lightbulb, MessageCircle, CheckCircle2, Send, UserCircle, MessageSquareOff, Loader2, Paperclip, X, ZoomIn, Trash2, Download, FileText } from 'lucide-react';
import TagBadge from '../TagBadge/TagBadge';

/** Convert epoch-seconds string → relative time label */
function timeAgo(epochStr: string | null | undefined): string {
    if (!epochStr) return '';
    const seconds = Number(epochStr);
    if (isNaN(seconds)) return '';
    const now = Math.floor(Date.now() / 1000);
    const diff = now - seconds;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    const d = new Date(1970, 0, 1);
    d.setUTCSeconds(seconds);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const CATEGORY_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: typeof Bug }> = {
    bug:      { label: 'Bug',      color: 'var(--danger)',  bgColor: 'var(--danger-soft, #fef2f2)',  icon: Bug },
    request:  { label: 'Request',  color: 'var(--info)',    bgColor: 'var(--info-soft, #eff6ff)',    icon: HelpCircle },
    feature:  { label: 'Feature',  color: 'var(--success)', bgColor: 'var(--success-soft, #f0fdf4)', icon: Lightbulb },
    comment:  { label: 'Comment',  color: 'var(--accent)',  bgColor: 'var(--accent-soft)',           icon: MessageCircle },
    resolved: { label: 'Resolved', color: 'var(--success)', bgColor: 'var(--success-soft, #f0fdf4)', icon: CheckCircle2 },
};

const CATEGORY_OPTIONS = Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => ({
    key,
    ...cfg,
}));

function getCategoryFromTags(tags: Array<{ name?: string | null; type?: string | null } | null> | null | undefined) {
    if (!tags) return null;
    for (const tag of tags) {
        const key = tag?.name?.toLowerCase();
        if (key && CATEGORY_CONFIG[key] && tag?.type?.toLowerCase() === 'feedback') {
            return { key, ...CATEGORY_CONFIG[key] };
        }
    }
    return null;
}

type FlatComment = {
    id: string;
    historyName: string;
    message: string;
    createdTime: string;
    authorName: string;
    authorId: string;
    category: ReturnType<typeof getCategoryFromTags>;
    versionId: string;
    versionName: string;
    attachmentUrl: string | null;
    attachmentName: string;
    attachmentExtension: string;
    tags: Array<{ id: string; name: string; type: string }>;
};

const IMAGE_EXTENSIONS = new Set(['.avif', '.bmp', '.gif', '.jpeg', '.jpg', '.png', '.svg', '.webp']);

function isImageAttachment(name: string, extension: string): boolean {
    const ext = (extension || name.slice(name.lastIndexOf('.'))).toLowerCase();
    return IMAGE_EXTENSIONS.has(ext.startsWith('.') ? ext : `.${ext}`);
}

type CommentsPanelProps = {
    contentId: string;
    currentVersionId?: string | null;
};

const CommentsPanel = ({ contentId, currentVersionId }: CommentsPanelProps) => {
    const currentUser = useCurrentUser();
    const [newMessage, setNewMessage] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('comment');
    const [submitting, setSubmitting] = useState(false);
    const [pendingImage, setPendingImage] = useState<File | null>(null);
    const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);

    const { data, loading, refetch } = useGetContentCommentsQuery({
        variables: { contentId },
        skip: !contentId,
    });

    const isAdmin = currentUser?.role === UserRoleEnum.Admin;

    const [addHistory] = useAddContentHistoryMutation();
    const [addHistoryTags] = useAddContentHistoryTagsMutation();
    const [uploadAttachment] = useUploadHistoryAttachmentMutation();
    const [deleteHistory] = useDeleteContentHistoryMutation();

    const handleDeleteComment = useCallback(async (historyId: string, contentVersionId: string) => {
        if (!window.confirm('Are you sure you want to delete this comment? This cannot be undone.')) return;
        try {
            await deleteHistory({ variables: { contentVersionId, ids: [historyId] } });
            refetch();
        } catch (err) {
            console.error('Failed to delete comment:', err);
        }
    }, [deleteHistory, refetch]);

    const downloadAttachment = useCallback(async (url: string, name: string) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Download failed (${response.status})`);
            const blobUrl = URL.createObjectURL(await response.blob());
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = name || 'attachment';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Failed to download attachment:', error);
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    }, []);

    // Query tags so we can resolve category name → tag ID
    const tagsQuery = useGetTagListWithTypeQuery({ variables: { contains: '', type: 'Feedback' } });
    const categoryTagMap = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        const nodes = tagsQuery.data?.TagList?.nodes;
        if (!nodes) return map;
        for (const tag of nodes) {
            const key = tag?.name?.toLowerCase();
            if (key && CATEGORY_CONFIG[key] && tag?.id) {
                map[key] = tag.id;
            }
        }
        return map;
    }, [tagsQuery.data]);

    // Build a map of tag ID → category config for fallback resolution from tagIds
    const tagIdToCategoryMap = useMemo<Record<string, { key: string; label: string; color: string; bgColor: string; icon: typeof Bug }>>(() => {
        const map: Record<string, { key: string; label: string; color: string; bgColor: string; icon: typeof Bug }> = {};
        const nodes = tagsQuery.data?.TagList?.nodes;
        if (!nodes) return map;
        for (const tag of nodes) {
            const key = tag?.name?.toLowerCase();
            if (key && CATEGORY_CONFIG[key] && tag?.id) {
                map[tag.id] = { key, ...CATEGORY_CONFIG[key] };
            }
        }
        return map;
    }, [tagsQuery.data]);

    // Flatten all histories from all versions into a single ticket timeline, oldest first.
    const comments = useMemo<FlatComment[]>(() => {
        const nodes = data?.ContentVersionList?.nodes;
        if (!nodes) return [];
        const flat: FlatComment[] = [];
        for (const version of nodes) {
            if (!version?.histories) continue;
            for (const h of version.histories) {
                if (!h?.id || (!h.message && !h.name)) continue;
                // Try resolved tags first, fall back to tagIds lookup
                let category = getCategoryFromTags(h.tags);
                if (!category && (h as any).tagIds) {
                    for (const tid of (h as any).tagIds) {
                        if (tid && tagIdToCategoryMap[tid]) {
                            category = tagIdToCategoryMap[tid];
                            break;
                        }
                    }
                }
                flat.push({
                    id: h.id,
                    historyName: h.name || '',
                    message: h.message || '',
                    createdTime: h.createdTime || '',
                    authorName: h.createdBy?.name || 'Unknown',
                    authorId: h.createdBy?.id || (h as any).createdById || '',
                    category,
                    versionId: version.id || '',
                    versionName: version.name || '',
                    attachmentUrl: h.attachmentDownloadUrl || h.attachment?.downloadUrl || null,
                    attachmentName: h.attachment?.name || 'Download attachment',
                    attachmentExtension: h.attachment?.extension || '',
                    tags: (h.tags ?? [])
                        .filter((tag): tag is NonNullable<typeof tag> => !!tag?.name)
                        .map(tag => ({ id: tag.id || tag.name || '', name: tag.name || '', type: tag.type || '' })),
                });
            }
        }
        // Ticket/chat convention: the newest activity sits beside the message entry.
        flat.sort((a, b) => Number(a.createdTime || 0) - Number(b.createdTime || 0));
        return flat;
    }, [data, tagIdToCategoryMap]);

    useEffect(() => {
        const timeline = timelineRef.current;
        if (timeline && !loading) timeline.scrollTop = timeline.scrollHeight;
    }, [comments.length, loading]);

    /** Attach an image file (from paste or file picker) */
    const attachImage = useCallback((file: File) => {
        // Only allow image types
        if (!file.type.startsWith('image/')) return;
        // Cap at 10 MB
        if (file.size > 10 * 1024 * 1024) {
            alert('Image must be smaller than 10 MB');
            return;
        }
        setPendingImage(file);
        const url = URL.createObjectURL(file);
        setPendingImagePreview(url);
    }, []);

    const removeImage = useCallback(() => {
        if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
        setPendingImage(null);
        setPendingImagePreview(null);
    }, [pendingImagePreview]);

    /** Handle clipboard paste — intercept images */
    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                e.preventDefault();
                const file = items[i].getAsFile();
                if (file) attachImage(file);
                return;
            }
        }
    }, [attachImage]);

    const handleSubmit = async () => {
        if ((!newMessage.trim() && !pendingImage) || !currentVersionId) return;
        setSubmitting(true);
        try {
            const tagId = categoryTagMap[selectedCategory];
            // Generate a client-side ID so we can reference it for tagging
            const historyId = crypto.randomUUID();
            // Step 1: Create the history entry with generated ID and author
            await addHistory({
                variables: {
                    contentVersionId: currentVersionId,
                    history: {
                        id: historyId,
                        message: newMessage.trim() || (pendingImage ? '📎 Image attached' : ''),
                    },
                },
            });
            // Step 2: Attach the category tag using the same ID
            if (tagId) {
                try {
                    await addHistoryTags({
                        variables: {
                            contentVersionId: currentVersionId,
                            historyId,
                            tagIds: [tagId],
                        },
                    });
                } catch (tagErr: any) {
                    console.error('Failed to add tags:', tagErr);
                }
            }
            // Step 3: Upload the image attachment if present
            if (pendingImage) {
                try {
                    const uploadResult = await uploadAttachment({
                        variables: {
                            contentVersionId: currentVersionId,
                            historyId,
                            file: pendingImage,
                        },
                    });
                    console.log('Upload result:', JSON.stringify(uploadResult.data));
                    if (!uploadResult.data?.ContentVersion?.uploadHistoryAttachment?.id) {
                        console.warn('Upload returned no attachment — historyId may not have matched. historyId used:', historyId);
                    }
                } catch (uploadErr: any) {
                    console.error('Failed to upload attachment:', uploadErr);
                    if (uploadErr?.networkError?.result) console.error('Upload network error:', JSON.stringify(uploadErr.networkError.result, null, 2));
                    if (uploadErr?.graphQLErrors) console.error('Upload GQL errors:', JSON.stringify(uploadErr.graphQLErrors, null, 2));
                }
            }
            setNewMessage('');
            removeImage();
            refetch();
        } catch (err: any) {
            console.error('Failed to add comment:', err);
            if (err?.graphQLErrors) console.error('GraphQL errors:', JSON.stringify(err.graphQLErrors, null, 2));
            if (err?.networkError?.result) console.error('Network error details:', JSON.stringify(err.networkError.result, null, 2));
            if (err?.networkError?.statusCode) console.error('Status code:', err.networkError.statusCode);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col h-full" style={{ borderLeft: '1px solid var(--border-default)' }}>
            {/* Header */}
            <div
                className="flex items-center gap-2 px-4 py-3 shrink-0"
                style={{ borderBottom: '1px solid var(--border-default)', backgroundColor: 'var(--surface-50)' }}
            >
                <MessageCircle size={15} style={{ color: 'var(--accent)' }} />
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Activity
                </span>
                {comments.length > 0 && (
                    <span
                        className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}
                    >
                        {comments.length}
                    </span>
                )}
            </div>

            {/* Comments timeline */}
            <div ref={timelineRef} className="flex-1 overflow-y-auto px-4 py-3" style={{ minHeight: 0 }}>
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                    </div>
                ) : comments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full gap-2" style={{ color: 'var(--text-tertiary)' }}>
                        <MessageSquareOff size={32} strokeWidth={1} />
                        <span className="text-sm">No activity yet</span>
                        <span className="text-xs">Start a conversation</span>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {comments.map((comment) => {
                            const isCurrentUser = comment.authorId === currentUser?.id;
                            const category = comment.category;
                            const CatIcon = category?.icon;
                            return (
                                <div key={comment.id} className="flex gap-2.5">
                                    {/* Avatar */}
                                    <div
                                        className="shrink-0 flex items-center justify-center rounded-full"
                                        style={{
                                            width: 28,
                                            height: 28,
                                            backgroundColor: isCurrentUser ? 'var(--accent-soft)' : 'var(--surface-200, #e5e7eb)',
                                            color: isCurrentUser ? 'var(--accent)' : 'var(--text-tertiary)',
                                            marginTop: 2,
                                        }}
                                    >
                                        <UserCircle size={16} />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                {comment.authorName}
                                            </span>
                                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                                {timeAgo(comment.createdTime)}
                                            </span>
                                            {isAdmin && (
                                                <button
                                                    onClick={() => handleDeleteComment(comment.id, comment.versionId)}
                                                    title="Delete comment"
                                                    className="ml-auto p-1 rounded transition-colors"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center' }}
                                                    onMouseOver={(e) => { e.currentTarget.style.color = 'var(--danger)'; }}
                                                    onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>

                                         {/* Category badge */}
                                        {comment.historyName && (
                                            <span
                                                className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-medium mb-1 mr-1"
                                                style={{
                                                    color: 'var(--text-secondary)',
                                                    backgroundColor: 'var(--surface-100)',
                                                    border: '1px solid var(--border-default)',
                                                }}
                                            >
                                                <FileText size={10} />
                                                {comment.historyName}
                                            </span>
                                        )}
                                        {category && CatIcon && (
                                            <span
                                                className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-medium mb-1"
                                                style={{ backgroundColor: category.bgColor, color: category.color }}
                                            >
                                                <CatIcon size={10} />
                                                {category.label}
                                            </span>
                                        )}

                                        {comment.tags.some(tag => !category || tag.name.toLowerCase() !== category.key) && (
                                            <div className="flex flex-wrap gap-1 mb-1">
                                                {comment.tags
                                                    .filter(tag => !category || tag.name.toLowerCase() !== category.key)
                                                    .map(tag => (
                                                        <TagBadge
                                                            key={tag.id || `${tag.type}:${tag.name}`}
                                                            tag={tag}
                                                            showType={false}
                                                            className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-full"
                                                            title={tag.type || 'Tag'}
                                                        >
                                                            {tag.name}
                                                        </TagBadge>
                                                    ))}
                                            </div>
                                        )}

                                        {/* Message */}
                                        <p
                                            className="text-sm leading-relaxed m-0"
                                            style={{ color: 'var(--text-secondary)', wordBreak: 'break-word' }}
                                        >
                                            {comment.message}
                                        </p>

                                        {/* Image preview or downloadable non-image attachment */}
                                        {comment.attachmentUrl && isImageAttachment(comment.attachmentName, comment.attachmentExtension) && (
                                            <div
                                                className="mt-1.5 relative group inline-block cursor-pointer"
                                                onClick={() => setLightboxUrl(comment.attachmentUrl)}
                                            >
                                                <img
                                                    src={comment.attachmentUrl}
                                                    alt={comment.attachmentName}
                                                    className="rounded-lg border"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: 200,
                                                        objectFit: 'cover',
                                                        borderColor: 'var(--border-default)',
                                                    }}
                                                />
                                                <div
                                                    className="absolute inset-0 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                    style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
                                                >
                                                    <ZoomIn size={20} style={{ color: 'white' }} />
                                                </div>
                                            </div>
                                        )}
                                        {comment.attachmentUrl && !isImageAttachment(comment.attachmentName, comment.attachmentExtension) && (
                                            <button
                                                type="button"
                                                onClick={() => downloadAttachment(comment.attachmentUrl!, comment.attachmentName)}
                                                className="mt-2 flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs no-underline"
                                                style={{
                                                    width: '100%',
                                                    color: 'var(--accent)',
                                                    backgroundColor: 'var(--accent-soft)',
                                                    border: '1px solid var(--border-default)',
                                                    cursor: 'pointer',
                                                    textAlign: 'left',
                                                }}
                                                title={`Download ${comment.attachmentName}`}
                                            >
                                                <FileText size={16} className="shrink-0" />
                                                <span className="flex-1 min-w-0 truncate">{comment.attachmentName}</span>
                                                <Download size={14} className="shrink-0" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* New comment form */}
            <div
                className="shrink-0 px-4 py-3"
                style={{ borderTop: '1px solid var(--border-default)', backgroundColor: 'var(--surface-50)' }}
            >
                {/* Category selector */}
                <div className="flex flex-wrap gap-1 mb-2">
                    {CATEGORY_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const isSelected = selectedCategory === opt.key;
                        return (
                            <button
                                key={opt.key}
                                onClick={() => setSelectedCategory(opt.key)}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-all"
                                style={{
                                    backgroundColor: isSelected ? opt.bgColor : 'transparent',
                                    color: isSelected ? opt.color : 'var(--text-tertiary)',
                                    border: `1px solid ${isSelected ? opt.color : 'var(--border-default)'}`,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                    fontWeight: isSelected ? 600 : 400,
                                    opacity: isSelected ? 1 : 0.7,
                                }}
                            >
                                <Icon size={11} />
                                {opt.label}
                            </button>
                        );
                    })}
                </div>

                {/* Input + send */}
                {/* Pending image preview */}
                {pendingImagePreview && (
                    <div className="mb-2 relative inline-block">
                        <img
                            src={pendingImagePreview}
                            alt="Pending"
                            className="rounded-lg border"
                            style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'cover', borderColor: 'var(--border-default)' }}
                        />
                        <button
                            onClick={removeImage}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center border-none"
                            style={{ backgroundColor: 'var(--danger)', color: 'white', cursor: 'pointer', padding: 0 }}
                        >
                            <X size={12} />
                        </button>
                    </div>
                )}
                <div className="flex gap-2">
                    <input
                        type="text"
                        placeholder="Leave a comment…"
                        className="input-base flex-1 text-sm"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
                        onPaste={handlePaste}
                        disabled={submitting || !currentVersionId}
                    />
                    {/* File picker button */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) attachImage(f); e.target.value = ''; }}
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={submitting || !currentVersionId}
                        className="p-2 rounded-lg transition-colors"
                        title="Attach image"
                        style={{
                            backgroundColor: pendingImage ? 'var(--accent-soft)' : 'var(--surface-100, #f3f4f6)',
                            color: pendingImage ? 'var(--accent)' : 'var(--text-tertiary)',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        <Paperclip size={14} />
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={(!newMessage.trim() && !pendingImage) || submitting || !currentVersionId}
                        className="p-2 rounded-lg transition-colors"
                        style={{
                            backgroundColor: (newMessage.trim() || pendingImage) && !submitting ? 'var(--accent)' : 'var(--surface-200, #e5e7eb)',
                            color: (newMessage.trim() || pendingImage) && !submitting ? 'white' : 'var(--text-tertiary)',
                            border: 'none',
                            cursor: (newMessage.trim() || pendingImage) && !submitting ? 'pointer' : 'default',
                        }}
                    >
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                </div>
            </div>

            {/* Lightbox overlay */}
            {lightboxUrl && (
                <div
                    className="fixed inset-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999 }}
                    onClick={() => setLightboxUrl(null)}
                >
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={lightboxUrl}
                            alt="Full size"
                            style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 }}
                        />
                        <button
                            onClick={() => setLightboxUrl(null)}
                            className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center border-none"
                            style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', cursor: 'pointer' }}
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommentsPanel;
